import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DEFAULT_MOBILE_STYLES,
  parseMobileStyles,
  resolveElementStyle,
  buildBackgroundStyle,
  buildBannerStyle,
  buildBadgeStyle,
  buildPanelStyle,
  appendCacheBuster
} from '../utils/mobileStyleUtils';
import AssetSelectorModal from './AssetSelectorModal';
import GradientModal from './GradientModal';
import { showToast } from '../context/ModalDialogContext';
import MobileOperatorDashboard, { SAMPLE_OPERATOR_METRICS } from './mobile/MobileOperatorDashboard';
import MobileVaultCarousel from './mobile/MobileVaultCarousel';
import MobileFloatingTabBar from './mobile/MobileFloatingTabBar';
import MobileShopTab from './mobile/MobileShopTab';
import MobileClaimTab from './mobile/MobileClaimTab';
import MobileProfileTab from './mobile/MobileProfileTab';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

export const DEFAULT_PREVIEW_USERS = [
  {
    id: 'patrick',
    first_name: 'Patrick',
    last_name: '',
    email: 'patrick@fishermans-tale.com',
    role: 'OPERATOR',
    nickname: 'Patrick',
    company_name: "Fisherman's Tale"
  },
  {
    id: 'alex',
    first_name: 'Alex',
    last_name: 'Vance',
    email: 'alex@collector.io',
    role: 'END_USER',
    nickname: 'Alex',
    company_name: 'Angler VIP'
  },
  {
    id: 'admin',
    first_name: 'Admin',
    last_name: 'User',
    email: 'admin@trophycoin.io',
    role: 'TENANT_ADMIN',
    nickname: 'Admin',
    company_name: 'TrophyCoin'
  }
];

// Available page definitions per mode
const MODES_CONFIG = {
  end_user: {
    label: 'End User',
    description: 'Collector & trophy owner mobile web experience',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    pages: [
      { id: 'vault', label: 'Trophy Vault (Carousel)' },
      { id: 'card', label: 'Loaded Trophy Card' },
      { id: 'shop', label: 'Reward Shop' },
      { id: 'claim', label: 'Claim Physical Coin' },
      { id: 'profile', label: 'Collector Profile' },
      { id: 'login', label: 'Mobile Login & Sign In' }
    ]
  },
  operator: {
    label: 'Operator',
    description: 'Minting operator & allocation dashboard',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    pages: [
      { id: 'home', label: 'Operator Dashboard' },
      { id: 'vault', label: 'Allocated Vault' },
      { id: 'card', label: 'Quality Control Card' },
      { id: 'profile', label: 'Operator Profile' },
      { id: 'login', label: 'Mobile Login & Sign In' }
    ]
  },
  agent: {
    label: 'Agent',
    description: 'Field agent verification & terminal scanner',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    pages: [
      { id: 'verify', label: 'NFC Scan & Verify' },
      { id: 'orders', label: 'Field Handover Orders' },
      { id: 'profile', label: 'Agent Terminal Profile' },
      { id: 'login', label: 'Mobile Login & Sign In' }
    ]
  }
};

const ELEMENT_TABS = [
  { id: 'banner', label: 'Banner', icon: 'M4 6h16M4 12h16M4 18h16' },
  { id: 'floating_menus', label: 'Floating Menus', icon: 'M12 2a10 10 0 1 0 10 10H12V2z' },
  { id: 'background', label: 'Background', icon: 'M3 3h18v18H3z' },
  { id: 'badges', label: 'Badges', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
  { id: 'content_panels', label: 'Content Panels', icon: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z' },
  { id: 'typography', label: 'Typography', icon: 'M4 7V4h16v3M9 20h6M12 4v16' }
];

export default function MobileWebDesigner({
  selectedTenantId,
  isGlobalRole = false,
  tenantConfig,
  onStylesSaved
}) {
  // Mode & Page selection state
  const [currentMode, setCurrentMode] = useState('end_user');
  const [currentPage, setCurrentPage] = useState('vault');
  const [selectedElement, setSelectedElement] = useState('banner');
  const [hoveredElement, setHoveredElement] = useState(null);
  const [phoneWidth, setPhoneWidth] = useState(380); // 380px or 420px

  // Live Cache Buster state: active by default during styling sessions, can be toggled OFF or refreshed
  const [cacheBusterActive, setCacheBusterActive] = useState(true);
  const [cacheBusterKey, setCacheBusterKey] = useState(() => Date.now());

  // Helper to append dynamic cache buster parameter to asset URLs when active
  const withCacheBuster = useCallback((url) => {
    return cacheBusterActive ? appendCacheBuster(url, cacheBusterKey) : url;
  }, [cacheBusterActive, cacheBusterKey]);

  // Preview user state for rendering real world data
  const [userList, setUserList] = useState(DEFAULT_PREVIEW_USERS);
  const [selectedUser, setSelectedUser] = useState(DEFAULT_PREVIEW_USERS[0]);

  // Load tenant users for previewing real-world data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
        const params = new URLSearchParams();
        if (selectedTenantId) params.set('tenant_id', selectedTenantId);
        if (cacheBusterActive) params.set('_cb', String(cacheBusterKey));
        const queryStr = params.toString() ? `?${params.toString()}` : '';
        const headers = {
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Tenant-Domain': window.location.hostname
        };
        if (cacheBusterActive) {
          headers['X-Cache-Buster'] = 'true';
        }
        const res = await fetch(`${API_BASE}/api/v1/users${queryStr}`, {
          headers
        });
        if (res.ok) {
          const data = await res.json();
          const usersArr = Array.isArray(data) ? data : (data?.items || data?.users || []);
          if (usersArr.length > 0) {
            const merged = [...usersArr];
            if (!merged.some(u => (u.first_name || '').toLowerCase() === 'patrick')) {
              merged.unshift(DEFAULT_PREVIEW_USERS[0]);
            }
            setUserList(merged);
          }
        }
      } catch (err) {
        // Fall back to DEFAULT_PREVIEW_USERS
      }
    };
    fetchUsers();
  }, [selectedTenantId, cacheBusterActive, cacheBusterKey]);

  const handleUserChange = (userId) => {
    const found = userList.find(u => String(u.id) === String(userId)) || DEFAULT_PREVIEW_USERS[0];
    setSelectedUser(found);
    if (['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN'].includes(found.role)) {
      if (currentMode !== 'operator') {
        setCurrentMode('operator');
        setCurrentPage('home');
      }
    } else if (found.role === 'END_USER') {
      if (currentMode !== 'end_user') {
        setCurrentMode('end_user');
        setCurrentPage('vault');
      }
    }
  };

  // Full styles state tree
  const [styles, setStyles] = useState(() => {
    const raw = tenantConfig?.mobile_styles;
    return parseMobileStyles(raw);
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [assetModalTarget, setAssetModalTarget] = useState(null);
  const [isGradientModalOpen, setIsGradientModalOpen] = useState(false);
  const [gradientModalTarget, setGradientModalTarget] = useState(null);

  // Sync with incoming tenantConfig when changed
  useEffect(() => {
    if (tenantConfig?.mobile_styles) {
      setStyles(parseMobileStyles(tenantConfig.mobile_styles));
    }
  }, [tenantConfig?.mobile_styles]);

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (saveSuccess) showToast(saveSuccess, 'success');
  }, [saveSuccess]);

  useEffect(() => {
    if (saveError) showToast(saveError, 'error');
  }, [saveError]);

  // When switching modes, select first valid page of that mode
  const handleModeChange = (mode) => {
    setCurrentMode(mode);
    const firstPage = MODES_CONFIG[mode]?.pages[0]?.id || 'vault';
    setCurrentPage(firstPage);
  };

  const pageKey = `${currentMode}:${currentPage}`;

  // Resolve current active styles for all elements
  const currentBanner = useMemo(() => resolveElementStyle(styles, pageKey, 'banner'), [styles, pageKey]);
  const currentBg = useMemo(() => resolveElementStyle(styles, pageKey, 'background'), [styles, pageKey]);
  const currentBadges = useMemo(() => resolveElementStyle(styles, pageKey, 'badges'), [styles, pageKey]);
  const currentPanels = useMemo(() => resolveElementStyle(styles, pageKey, 'content_panels'), [styles, pageKey]);
  const currentTypo = useMemo(() => resolveElementStyle(styles, pageKey, 'typography'), [styles, pageKey]);

  // Active element configuration currently being edited in properties panel
  const activeElementState = useMemo(() => {
    return resolveElementStyle(styles, pageKey, selectedElement);
  }, [styles, pageKey, selectedElement]);

  // Update a single property on the active element
  const handlePropertyChange = useCallback((property, value) => {
    setStyles((prev) => {
      const isOverridden = Boolean(prev.pages?.[pageKey]?.overrides?.[selectedElement]);
      if (isOverridden) {
        // Update page-specific override
        const existingPage = prev.pages?.[pageKey] || { overrides: {} };
        const existingElement = existingPage[selectedElement] || { ...(prev.global?.[selectedElement] || {}) };
        return {
          ...prev,
          pages: {
            ...prev.pages,
            [pageKey]: {
              ...existingPage,
              overrides: {
                ...(existingPage.overrides || {}),
                [selectedElement]: true
              },
              [selectedElement]: {
                ...existingElement,
                [property]: value
              }
            }
          }
        };
      } else {
        // Update global style
        return {
          ...prev,
          global: {
            ...prev.global,
            [selectedElement]: {
              ...(prev.global?.[selectedElement] || {}),
              [property]: value
            }
          }
        };
      }
    });
  }, [pageKey, selectedElement]);

  // Toggle Global vs Page Override for the active element
  const handleToggleGlobal = useCallback((useGlobal) => {
    setStyles((prev) => {
      const existingPage = prev.pages?.[pageKey] || { overrides: {} };
      const currentElementStyle = resolveElementStyle(prev, pageKey, selectedElement).style;

      if (useGlobal) {
        // Switch back to global: remove page override flag
        const newOverrides = { ...(existingPage.overrides || {}) };
        delete newOverrides[selectedElement];
        const newPageData = { ...existingPage, overrides: newOverrides };
        delete newPageData[selectedElement];

        return {
          ...prev,
          pages: {
            ...prev.pages,
            [pageKey]: newPageData
          }
        };
      } else {
        // Decouple to page override: copy current values into page
        return {
          ...prev,
          pages: {
            ...prev.pages,
            [pageKey]: {
              ...existingPage,
              overrides: {
                ...(existingPage.overrides || {}),
                [selectedElement]: true
              },
              [selectedElement]: { ...currentElementStyle }
            }
          }
        };
      }
    });
  }, [pageKey, selectedElement]);

  // Save styles to backend
  const handleSaveStyles = async () => {
    const targetTenantId = selectedTenantId || tenantConfig?.id;
    if (!targetTenantId) {
      setSaveError('No tenant selected. Please select a tenant first.');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const payload = {
        mobile_styles: JSON.stringify(styles)
      };

      const queryParam = cacheBusterActive ? `?_cb=${cacheBusterKey}` : '';
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      if (cacheBusterActive) {
        headers['X-Cache-Buster'] = 'true';
      }

      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${targetTenantId}${queryParam}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server returned ${res.status}`);
      }

      // Automatically refresh cache buster timestamp after saving so next preview re-busts
      if (cacheBusterActive) {
        setCacheBusterKey(Date.now());
      }

      setSaveSuccess('Mobile Web styles successfully saved and synchronized! Cache updated.');
      if (onStylesSaved) onStylesSaved(styles);
      setTimeout(() => setSaveSuccess(''), 5000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save mobile styles.');
    } finally {
      setSaving(false);
    }
  };

  // Reset to default styles
  const handleResetToDefaults = () => {
    if (window.confirm('Reset all mobile styles to original defaults? This will erase current customizations.')) {
      setStyles(JSON.parse(JSON.stringify(DEFAULT_MOBILE_STYLES)));
      setSaveSuccess('Styles reset to defaults. Click "Save Changes" to commit.');
      setTimeout(() => setSaveSuccess(''), 4000);
    }
  };

  // Helper for interactive elements in the mockup
  const getInteractiveProps = (elemKey) => {
    const isSelected = selectedElement === elemKey;
    const isHovered = hoveredElement === elemKey;
    return {
      onClick: (e) => {
        e.stopPropagation();
        setSelectedElement(elemKey);
      },
      onMouseEnter: () => setHoveredElement(elemKey),
      onMouseLeave: () => setHoveredElement(null),
      style: {
        position: 'relative',
        cursor: 'pointer',
        outline: isSelected
          ? '2px solid #38bdf8'
          : isHovered
          ? '1.5px dashed rgba(56, 189, 248, 0.6)'
          : 'none',
        outlineOffset: '2px',
        transition: 'outline 0.15s ease',
        borderRadius: elemKey === 'floating_menus' ? '9999px' : '8px'
      }
    };
  };

  // Generate CSS objects for preview
  const canvasBgStyle = useMemo(() => buildBackgroundStyle(currentBg.style, cacheBusterActive ? cacheBusterKey : null), [currentBg.style, cacheBusterActive, cacheBusterKey]);
  const bannerComputedStyle = useMemo(() => buildBannerStyle(currentBanner.style), [currentBanner.style]);
  const badgeComputedStyle = useMemo(() => buildBadgeStyle(currentBadges.style), [currentBadges.style]);
  const panelComputedStyle = useMemo(() => buildPanelStyle(currentPanels.style), [currentPanels.style]);

  return (
    <div
      data-testid="mobile-web-designer-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '0'
      }}
    >
      {/* Top Studio Control Bar */}
      <div
        style={{
          background: 'var(--panel-color, var(--card-bg, #161b22))',
          border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
          borderRadius: '10px',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          {/* Mode Selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-mute, #8b949e)' }}>
              Audience Mode
            </span>
            <div
              style={{
                display: 'inline-flex',
                background: 'rgba(0, 0, 0, 0.25)',
                padding: '3px',
                borderRadius: '8px',
                border: '1px solid var(--border-color, rgba(255,255,255,0.08))'
              }}
            >
              {Object.entries(MODES_CONFIG).map(([key, config]) => {
                const isActive = currentMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`mode-btn-${key}`}
                    onClick={() => handleModeChange(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      fontSize: '12.5px',
                      fontWeight: isActive ? '700' : '500',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      background: isActive ? 'var(--primary-color, #2563eb)' : 'transparent',
                      color: isActive ? '#ffffff' : 'var(--text-mute, #94a3b8)',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {config.icon}
                    <span>{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Page Selector Dropdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-mute, #8b949e)' }}>
              Page Viewer
            </span>
            <select
              data-testid="mobile-page-select"
              value={currentPage}
              onChange={(e) => setCurrentPage(e.target.value)}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '6px',
                background: 'var(--input-bg, #0d1117)',
                color: 'var(--input-text, #f0f6fc)',
                border: '1px solid var(--input-border, rgba(255,255,255,0.15))',
                fontSize: '13px',
                fontWeight: '600',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '210px'
              }}
            >
              {MODES_CONFIG[currentMode]?.pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* User Preview Selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-mute, #8b949e)' }}>
              Preview User
            </span>
            <select
              data-testid="preview-user-select"
              value={selectedUser?.id || ''}
              onChange={(e) => handleUserChange(e.target.value)}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '6px',
                background: 'var(--input-bg, #0d1117)',
                color: 'var(--input-text, #f0f6fc)',
                border: '1px solid var(--input-border, rgba(255,255,255,0.15))',
                fontSize: '13px',
                fontWeight: '600',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '200px'
              }}
            >
              {userList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name || u.email?.split('@')[0]} ({u.role || 'USER'})
                </option>
              ))}
            </select>
          </div>

          {/* Phone Size Toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-mute, #8b949e)' }}>
              Device Size
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                type="button"
                onClick={() => setPhoneWidth(380)}
                style={{
                  height: '36px',
                  padding: '0 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: phoneWidth === 380 ? '700' : '500',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  background: phoneWidth === 380 ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                  color: phoneWidth === 380 ? '#38bdf8' : 'var(--text-mute)',
                  cursor: 'pointer'
                }}
              >
                Standard (380px)
              </button>
              <button
                type="button"
                onClick={() => setPhoneWidth(420)}
                style={{
                  height: '36px',
                  padding: '0 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: phoneWidth === 420 ? '700' : '500',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  background: phoneWidth === 420 ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                  color: phoneWidth === 420 ? '#38bdf8' : 'var(--text-mute)',
                  cursor: 'pointer'
                }}
              >
                Max (420px)
              </button>
            </div>
          </div>

          {/* Live Cache Buster Toggle & Key Generator */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-mute, #8b949e)' }}>
                Cache Buster
              </span>
              {cacheBusterActive && (
                <span style={{ fontSize: '9.5px', color: '#38bdf8', fontWeight: '800', letterSpacing: '0.04em' }}>
                  ACTIVE
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <button
                type="button"
                data-testid="toggle-cache-buster-btn"
                onClick={() => setCacheBusterActive(prev => !prev)}
                title={cacheBusterActive ? 'Cache buster is ACTIVE. Fresh logos, banners, and styles bypass browser cache.' : 'Cache buster is OFF. Standard caching enabled for normal use.'}
                style={{
                  height: '36px',
                  padding: '0 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: cacheBusterActive ? '1px solid rgba(56, 189, 248, 0.45)' : '1px solid var(--border-color, rgba(255,255,255,0.15))',
                  background: cacheBusterActive ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                  color: cacheBusterActive ? '#38bdf8' : 'var(--text-mute, #94a3b8)',
                  boxShadow: cacheBusterActive ? '0 0 10px rgba(56, 189, 248, 0.2)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: cacheBusterActive ? '#10b981' : '#64748b',
                    boxShadow: cacheBusterActive ? '0 0 8px #10b981' : 'none'
                  }}
                />
                <span>{cacheBusterActive ? '⚡ Buster: ON' : 'Buster: OFF'}</span>
              </button>

              {cacheBusterActive && (
                <button
                  type="button"
                  data-testid="refresh-cache-buster-btn"
                  onClick={() => {
                    setCacheBusterKey(Date.now());
                    setSaveSuccess('⚡ Cache busted! Fresh assets & styling reloaded.');
                    setTimeout(() => setSaveSuccess(''), 3000);
                  }}
                  title="Force reload all preview assets (regenerate cache buster)"
                  style={{
                    height: '36px',
                    width: '36px',
                    padding: 0,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-color, rgba(255,255,255,0.15))',
                    color: '#cbd5e1',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons: Save & Reset */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={handleResetToDefaults}
            style={{
              height: '38px',
              padding: '0 14px',
              fontSize: '12.5px',
              fontWeight: '600',
              borderRadius: '6px',
              border: '1px solid var(--border-color, rgba(255,255,255,0.15))',
              background: 'transparent',
              color: 'var(--text-mute, #94a3b8)',
              cursor: 'pointer'
            }}
          >
            Reset Defaults
          </button>

          <button
            type="button"
            data-testid="save-mobile-styles-btn"
            disabled={saving}
            onClick={handleSaveStyles}
            style={{
              height: '38px',
              padding: '0 18px',
              fontSize: '13px',
              fontWeight: '700',
              borderRadius: '6px',
              border: 'none',
              background: 'var(--primary-color, #2563eb)',
              color: '#ffffff',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: saving ? 0.7 : 1,
              boxShadow: '0 2px 10px rgba(37, 99, 235, 0.35)'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>{saving ? 'Saving...' : 'Save Mobile Styles'}</span>
          </button>
        </div>
      </div>

      {/* Toast Feedback */}
      {saveSuccess && (
        <div
          data-testid="mobile-styles-success-toast"
          style={{
            padding: '10px 16px',
            borderRadius: '6px',
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid #10b981',
            color: '#34d399',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {saveSuccess}
        </div>
      )}

      {saveError && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: '6px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid #ef4444',
            color: '#f87171',
            fontSize: '13px',
            fontWeight: '600'
          }}
        >
          {saveError}
        </div>
      )}

      {/* Main Designer Grid: Phone Mockup on Left + Properties Panel on Right */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `minmax(320px, ${phoneWidth + 30}px) minmax(360px, 1fr)`,
          gap: '24px',
          alignItems: 'start'
        }}
      >
        {/* ============================================================== */}
        {/* LEFT COLUMN: REALISTIC MOBILE PHONE FRAME & LIVE MOCKUP VIEWER */}
        {/* ============================================================== */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'sticky',
            top: '20px'
          }}
        >
          <div
            style={{
              marginBottom: '8px',
              fontSize: '11px',
              color: 'var(--text-mute)',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
            <span>Interactive Live Canvas &bull; Click any element to edit</span>
          </div>

          {/* Phone Hardware Bezel */}
          <div
            style={{
              width: `${phoneWidth}px`,
              height: '740px',
              backgroundColor: '#1e232a',
              borderRadius: '46px',
              padding: '12px',
              boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 0 0 2px #0a0d11',
              position: 'relative',
              boxSizing: 'border-box'
            }}
          >
            {/* Phone Screen Glass */}
            <div
              data-testid="phone-screen-canvas"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '36px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: currentTypo.style.font_family || 'sans-serif',
                ...canvasBgStyle
              }}
            >
              {/* Dynamic Island / Camera Notch */}
              <div
                style={{
                  position: 'absolute',
                  top: '9px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '94px',
                  height: '24px',
                  backgroundColor: '#000000',
                  borderRadius: '14px',
                  zIndex: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  pointerEvents: 'none'
                }}
              >
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#071524' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#091f36', border: '1px solid #14283f' }} />
              </div>

              {/* Status Bar */}
              <div
                style={{
                  height: '40px',
                  padding: '0 22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '600',
                  zIndex: 90,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                <span>9:41</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="14" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="1" y="16" width="3" height="6" rx="1" />
                    <rect x="7" y="12" width="3" height="10" rx="1" />
                    <rect x="13" y="7" width="3" height="15" rx="1" />
                    <rect x="19" y="2" width="3" height="20" rx="1" />
                  </svg>
                  <svg width="14" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
                  </svg>
                  <svg width="18" height="10" viewBox="0 0 28 14" fill="currentColor">
                    <rect x="1" y="1" width="22" height="12" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
                    <rect x="3" y="3" width="14" height="8" rx="1.5" />
                    <path d="M25 5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              {/* Banner / Header Element (hidden on unauthenticated login screen) */}
              {currentPage !== 'login' && (
                <div
                  {...getInteractiveProps('banner')}
                  data-testid="interactive-mockup-banner"
                  style={{
                    ...getInteractiveProps('banner').style,
                    ...bannerComputedStyle,
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: currentBanner.style.alignment || 'space-between',
                    gap: '12px',
                    zIndex: 80
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Brand Logo or Icon */}
                    {currentBanner.style.custom_logo_url || tenantConfig?.logo_url ? (
                      <img
                        src={withCacheBuster(currentBanner.style.custom_logo_url || tenantConfig?.logo_url)}
                        alt="Brand Logo"
                        style={{
                          height: `${currentBanner.style.logo_height || 34}px`,
                          maxWidth: '120px',
                          objectFit: 'contain',
                          transform: `translate(${currentBanner.style.logo_offset_x || 0}px, ${currentBanner.style.logo_offset_y || 0}px)`
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: `${currentBanner.style.logo_height || 32}px`,
                          height: `${currentBanner.style.logo_height || 32}px`,
                          borderRadius: '8px',
                          backgroundColor: tenantConfig?.primary_color || '#d97706',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '800',
                          fontSize: '14px',
                          transform: `translate(${currentBanner.style.logo_offset_x || 0}px, ${currentBanner.style.logo_offset_y || 0}px)`
                        }}
                      >
                        TC
                      </div>
                    )}

                    <div>
                      <h1
                        style={{
                          margin: 0,
                          fontSize: `${currentBanner.style.title_font_size || 14}px`,
                          fontWeight: '800',
                          color: currentBanner.style.title_color || '#ffffff',
                          letterSpacing: `${currentBanner.style.title_letter_spacing || -0.2}px`,
                          lineHeight: 1.1
                        }}
                      >
                        {tenantConfig?.brand_name || 'TrophyCoin Brand'}
                      </h1>
                      <p
                        style={{
                          margin: '2px 0 0 0',
                          fontSize: '11px',
                          color: currentBanner.style.subtitle_color || '#94a3b8'
                        }}
                      >
                        Hi, <span style={{ color: currentBanner.style.highlight_color || '#fbbf24', fontWeight: '700' }}>
                          {selectedUser?.first_name || (currentMode === 'operator' ? 'Patrick' : 'Collector Alex')}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Right Header Status / Refresh & Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                        padding: '5px 7px',
                        color: '#cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      title="Reload"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                      </svg>
                    </div>
                    <span
                      {...getInteractiveProps('badges')}
                      style={{
                        ...getInteractiveProps('badges').style,
                        ...badgeComputedStyle,
                        padding: '3px 8px',
                        fontSize: '10px'
                      }}
                    >
                      {currentMode === 'operator' ? 'OPERATOR' : currentMode === 'agent' ? 'TERMINAL' : 'GOLD VIP'}
                    </span>
                  </div>
                </div>
              )}

              {/* Scrollable Page Content Mockup */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingTop: (currentPage === 'home' && currentMode === 'operator') || currentPage === 'login' ? '0' : '16px',
                  paddingLeft: (currentPage === 'home' && currentMode === 'operator') || currentPage === 'login' ? '0' : '16px',
                  paddingRight: (currentPage === 'home' && currentMode === 'operator') || currentPage === 'login' ? '0' : '16px',
                  paddingBottom: currentPage === 'login' ? '20px' : '90px', // Clearance for floating bottom menu
                  position: 'relative'
                }}
              >
                {/* Render specific real or mock screen based on currentMode & currentPage */}
                {renderMockScreenContent({
                  mode: currentMode,
                  page: currentPage,
                  getInteractiveProps,
                  panelStyle: panelComputedStyle,
                  badgeStyle: badgeComputedStyle,
                  typoStyle: currentTypo.style,
                  bannerStyle: currentBanner.style,
                  user: selectedUser,
                  tenant: tenantConfig,
                  mobileStyles: styles,
                  selectedElement,
                  onSelectElement: setSelectedElement,
                  onNavigate: (newPage) => setCurrentPage(newPage),
                  cacheBusterKey: cacheBusterActive ? cacheBusterKey : null,
                  withCacheBuster
                })}
              </div>

              {/* Bottom Floating Menu Element (hidden on unauthenticated login screen) */}
              {currentPage !== 'login' && (
                <div
                  {...getInteractiveProps('floating_menus')}
                  data-testid="interactive-mockup-floating-menu"
                  style={{
                    ...getInteractiveProps('floating_menus').style,
                    position: 'absolute',
                    bottom: '16px',
                    left: '16px',
                    right: '16px',
                    zIndex: 85
                  }}
                >
                  <MobileFloatingTabBar
                    activeTab={currentPage}
                    onSelectTab={(tab) => {
                      setCurrentPage(tab);
                      setSelectedElement('floating_menus');
                    }}
                    cartBadgeCount={currentMode === 'end_user' ? 1 : 0}
                    user={selectedUser}
                    isOperator={currentMode === 'operator'}
                    mobileStyles={styles}
                    style={{
                      position: 'relative',
                      bottom: 'auto',
                      left: 'auto',
                      transform: 'none',
                      width: '100%',
                      maxWidth: '100%',
                      zIndex: 'auto'
                    }}
                  />
                </div>
              )}

              {/* Home Swipe Indicator */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '5px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '120px',
                  height: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                  borderRadius: '2px',
                  pointerEvents: 'none',
                  zIndex: 90
                }}
              />
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* RIGHT COLUMN: PROPERTIES PANEL WITH ELEMENT TABS & SETTINGS */}
        {/* ============================================================== */}
        <div
          data-testid="mobile-properties-panel"
          style={{
            background: 'var(--panel-color, var(--card-bg, #161b22))',
            border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
            borderRadius: '10px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          {/* Properties Panel Header & Element Tabs */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main, #f0f6fc)', margin: 0 }}>
                  Styling Properties Panel
                </h2>
                <p style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)', margin: '3px 0 0 0' }}>
                  Target Element: <strong style={{ color: '#38bdf8' }}>{ELEMENT_TABS.find(t => t.id === selectedElement)?.label}</strong> &bull; Page: <span style={{ color: 'var(--text-main)' }}>{currentPage}</span>
                </p>
              </div>
            </div>

            {/* Element Navigation Tabs */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '6px',
                background: 'rgba(0,0,0,0.2)',
                padding: '4px',
                borderRadius: '8px',
                border: '1px solid var(--border-color, rgba(255,255,255,0.06))'
              }}
            >
              {ELEMENT_TABS.map((tab) => {
                const isActive = selectedElement === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`element-tab-${tab.id}`}
                    onClick={() => setSelectedElement(tab.id)}
                    style={{
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontWeight: isActive ? '700' : '500',
                      borderRadius: '6px',
                      border: 'none',
                      background: isActive ? 'var(--primary-color, #2563eb)' : 'transparent',
                      color: isActive ? '#ffffff' : 'var(--text-mute, #94a3b8)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Global Style vs Page Override Toggle */}
          <div
            style={{
              padding: '12px 14px',
              borderRadius: '8px',
              background: activeElementState.isGlobal
                ? 'rgba(56, 189, 248, 0.08)'
                : 'rgba(234, 179, 8, 0.1)',
              border: `1px solid ${activeElementState.isGlobal ? 'rgba(56, 189, 248, 0.25)' : 'rgba(234, 179, 8, 0.3)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: activeElementState.isGlobal ? '#38bdf8' : '#fbbf24' }}>
                {activeElementState.isGlobal ? 'Global Style Active' : 'Page-Specific Override Active'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', marginTop: '2px' }}>
                {activeElementState.isGlobal
                  ? `Changes to ${selectedElement} apply to all mobile pages by default.`
                  : `This style is decoupled and applies only to ${currentMode}:${currentPage}.`}
              </div>
            </div>

            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '12.5px',
                fontWeight: '600',
                color: 'var(--text-main, #f0f6fc)'
              }}
            >
              <input
                type="checkbox"
                data-testid="toggle-global-style"
                checked={activeElementState.isGlobal}
                onChange={(e) => handleToggleGlobal(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563eb' }}
              />
              <span>Use Global Style</span>
            </label>
          </div>

          {/* Dynamic Settings Fields for the Active Element */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {renderElementControls({
              element: selectedElement,
              state: activeElementState.style,
              onChange: handlePropertyChange,
              onOpenAssetModal: (target) => {
                setAssetModalTarget(target);
                setIsAssetModalOpen(true);
              },
              onOpenGradientModal: (target, currentVal) => {
                setGradientModalTarget({ target, value: currentVal });
                setIsGradientModalOpen(true);
              },
              withCacheBuster
            })}
          </div>
        </div>
      </div>

      {/* Asset Selector Modal Integration */}
      {isAssetModalOpen && (
        <AssetSelectorModal
          isOpen={isAssetModalOpen}
          onClose={() => {
            setIsAssetModalOpen(false);
            setAssetModalTarget(null);
          }}
          title={assetModalTarget === 'custom_logo_url' ? 'Select Custom Mobile Banner Logo' : 'Select Mobile Asset'}
          currentValue={activeElementState.style[assetModalTarget] || ''}
          tenantId={selectedTenantId || tenantConfig?.id}
          assetType="image"
          cacheBusterKey={cacheBusterActive ? cacheBusterKey : null}
          onSelect={(url) => {
            if (assetModalTarget) {
              handlePropertyChange(assetModalTarget, url);
            }
            setIsAssetModalOpen(false);
            setAssetModalTarget(null);
          }}
        />
      )}

      {/* Gradient Picker Modal Integration */}
      {isGradientModalOpen && (
        <GradientModal
          isOpen={isGradientModalOpen}
          onClose={() => {
            setIsGradientModalOpen(false);
            setGradientModalTarget(null);
          }}
          value={gradientModalTarget?.value || 'linear-gradient(90deg, #111827 0%, #0a0e17 100%)'}
          onApply={(newGradient) => {
            if (gradientModalTarget?.target) {
              handlePropertyChange(gradientModalTarget.target, newGradient);
            }
            setIsGradientModalOpen(false);
            setGradientModalTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ELEMENT CONTROLS SUB-FORM (Fills, Gradients, Borders, Shadows, Glows, etc.)
// ============================================================================
function renderElementControls({ element, state, onChange, onOpenAssetModal, onOpenGradientModal, withCacheBuster }) {
  switch (element) {
    case 'banner':
      return (
        <>
          <SectionHeader title="Banner Fill & Glassmorphism" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <SelectField
              label="Fill Mode"
              value={state.bg_type || 'glass'}
              options={[
                { value: 'glass', label: 'Glassmorphism (Frosted)' },
                { value: 'solid', label: 'Solid Color' },
                { value: 'gradient', label: 'Gradient' }
              ]}
              onChange={(v) => onChange('bg_type', v)}
            />
            {state.bg_type === 'gradient' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={labelStyle}>Gradient Fill</label>
                <button
                  type="button"
                  onClick={() => onOpenGradientModal('bg_gradient', state.bg_gradient)}
                  style={buttonStyle}
                >
                  Edit CSS Gradient
                </button>
              </div>
            ) : (
              <ColorField
                label="Background Color"
                value={state.bg_color || '#0a0e17'}
                onChange={(v) => onChange('bg_color', v)}
              />
            )}
          </div>

          {state.bg_type === 'glass' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <RangeField
                label="Opacity"
                min={20}
                max={100}
                unit="%"
                value={state.opacity ?? 88}
                onChange={(v) => onChange('opacity', v)}
              />
              <RangeField
                label="Backdrop Blur"
                min={0}
                max={30}
                unit="px"
                value={state.blur ?? 12}
                onChange={(v) => onChange('blur', v)}
              />
            </div>
          )}

          <SectionHeader title="Custom Logo & Brand Assets" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={labelStyle}>Banner Logo Asset</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Asset URL (or choose from library)"
                value={state.custom_logo_url || ''}
                onChange={(e) => onChange('custom_logo_url', e.target.value)}
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => onOpenAssetModal('custom_logo_url')}
                style={{ ...buttonStyle, minWidth: '120px' }}
              >
                Browse Assets
              </button>
            </div>
            {state.custom_logo_url && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Preview:</span>
                <img
                  src={withCacheBuster ? withCacheBuster(state.custom_logo_url) : state.custom_logo_url}
                  alt="Logo Preview"
                  style={{ height: '24px', maxWidth: '80px', objectFit: 'contain' }}
                />
                <button
                  type="button"
                  onClick={() => onChange('custom_logo_url', '')}
                  style={{ background: 'none', border: 'none', color: '#f87171', fontSize: '11px', cursor: 'pointer', marginLeft: 'auto' }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <RangeField
              label="Logo Height"
              min={20}
              max={64}
              unit="px"
              value={state.logo_height ?? 34}
              onChange={(v) => onChange('logo_height', v)}
            />
            <RangeField
              label="Offset X"
              min={-40}
              max={40}
              unit="px"
              value={state.logo_offset_x ?? 0}
              onChange={(v) => onChange('logo_offset_x', v)}
            />
            <RangeField
              label="Offset Y"
              min={-20}
              max={20}
              unit="px"
              value={state.logo_offset_y ?? 0}
              onChange={(v) => onChange('logo_offset_y', v)}
            />
          </div>

          <SectionHeader title="Banner Typography & Colors" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <ColorField
              label="Title Color"
              value={state.title_color || '#ffffff'}
              onChange={(v) => onChange('title_color', v)}
            />
            <ColorField
              label="Subtitle Color"
              value={state.subtitle_color || '#94a3b8'}
              onChange={(v) => onChange('subtitle_color', v)}
            />
            <ColorField
              label="Highlight Color"
              value={state.highlight_color || '#fbbf24'}
              onChange={(v) => onChange('highlight_color', v)}
            />
          </div>

          <SectionHeader title="Border & Elevation" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Bottom Border Width"
              min={0}
              max={6}
              unit="px"
              value={state.border_width ?? 1}
              onChange={(v) => onChange('border_width', v)}
            />
            <ColorField
              label="Border Color"
              value={state.border_color || 'rgba(255, 255, 255, 0.08)'}
              onChange={(v) => onChange('border_color', v)}
            />
          </div>
        </>
      );

    case 'floating_menus':
      return (
        <>
          <SectionHeader title="Pill Bar Geometry & Position" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Corner Radius"
              min={12}
              max={9999}
              unit="px"
              value={state.border_radius ?? 9999}
              onChange={(v) => onChange('border_radius', v)}
            />
            <SelectField
              label="Fill Mode"
              value={state.bg_type || 'glass'}
              options={[
                { value: 'glass', label: 'Glassmorphism' },
                { value: 'solid', label: 'Solid' },
                { value: 'gradient', label: 'Gradient' }
              ]}
              onChange={(v) => onChange('bg_type', v)}
            />
          </div>

          {state.bg_type === 'gradient' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>Gradient Fill</label>
              <button
                type="button"
                onClick={() => onOpenGradientModal('bg_gradient', state.bg_gradient)}
                style={buttonStyle}
              >
                Edit CSS Gradient
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <ColorField
                label="Menu Background"
                value={state.bg_color || '#111827'}
                onChange={(v) => onChange('bg_color', v)}
              />
              <RangeField
                label="Opacity"
                min={30}
                max={100}
                unit="%"
                value={state.opacity ?? 88}
                onChange={(v) => onChange('opacity', v)}
              />
            </div>
          )}

          <SectionHeader title="Active & Inactive Tab Colors" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <ColorField
              label="Active Tab Accent"
              value={state.active_tab_color || '#38bdf8'}
              onChange={(v) => onChange('active_tab_color', v)}
            />
            <ColorField
              label="Active Pill Fill"
              value={state.active_tab_bg || 'rgba(56, 189, 248, 0.14)'}
              onChange={(v) => onChange('active_tab_bg', v)}
            />
            <ColorField
              label="Inactive Tab Color"
              value={state.inactive_tab_color || '#94a3b8'}
              onChange={(v) => onChange('inactive_tab_color', v)}
            />
          </div>

          <SectionHeader title="Menu Border & Glow" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Border Width"
              min={0}
              max={4}
              unit="px"
              value={state.border_width ?? 1}
              onChange={(v) => onChange('border_width', v)}
            />
            <ColorField
              label="Border Color"
              value={state.border_color || 'rgba(255, 255, 255, 0.12)'}
              onChange={(v) => onChange('border_color', v)}
            />
          </div>

          <SelectField
            label="Ambient Glow"
            value={state.glow || 'none'}
            options={[
              { value: 'none', label: 'None' },
              { value: '0 0 15px rgba(56, 189, 248, 0.25)', label: 'Cyan Aura (0 0 15px)' },
              { value: '0 0 20px rgba(251, 191, 36, 0.25)', label: 'Gold Glow (0 0 20px)' },
              { value: '0 0 20px rgba(16, 185, 129, 0.25)', label: 'Emerald Glow' }
            ]}
            onChange={(v) => onChange('glow', v)}
          />
        </>
      );

    case 'background':
      return (
        <>
          <SectionHeader title="Background Canvas Type" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <SelectField
              label="Type"
              value={state.type || 'pattern'}
              options={[
                { value: 'pattern', label: 'Pattern Texture' },
                { value: 'gradient', label: 'Gradient' },
                { value: 'solid', label: 'Solid Color' }
              ]}
              onChange={(v) => onChange('type', v)}
            />
            <ColorField
              label="Base Canvas Color"
              value={state.color || '#0a0e17'}
              onChange={(v) => onChange('color', v)}
            />
          </div>

          {state.type === 'pattern' && (
            <>
              <SectionHeader title="Pattern Settings" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <SelectField
                  label="Pattern Texture"
                  value={state.pattern || 'dots'}
                  options={[
                    { value: 'dots', label: 'Subtle Dot Matrix' },
                    { value: 'grid', label: 'Technical Grid' },
                    { value: 'stripes', label: 'Diagonal Stripes' },
                    { value: 'carbon', label: 'Carbon Weave' },
                    { value: 'none', label: 'None' }
                  ]}
                  onChange={(v) => onChange('pattern', v)}
                />
                <RangeField
                  label="Pattern Intensity"
                  min={5}
                  max={100}
                  unit="%"
                  value={state.pattern_opacity ?? 25}
                  onChange={(v) => onChange('pattern_opacity', v)}
                />
              </div>
            </>
          )}

          {state.type === 'gradient' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>Gradient</label>
              <button
                type="button"
                onClick={() => onOpenGradientModal('gradient', state.gradient)}
                style={buttonStyle}
              >
                Edit CSS Canvas Gradient
              </button>
            </div>
          )}
        </>
      );

    case 'badges':
      return (
        <>
          <SectionHeader title="Badge Fill & Colors" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <SelectField
              label="Fill Mode"
              value={state.bg_type || 'solid'}
              options={[
                { value: 'solid', label: 'Solid Color' },
                { value: 'gradient', label: 'Gradient' },
                { value: 'glass', label: 'Glassmorphism' }
              ]}
              onChange={(v) => onChange('bg_type', v)}
            />
            <ColorField
              label="Badge Background"
              value={state.bg_color || '#10b981'}
              onChange={(v) => onChange('bg_color', v)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <ColorField
              label="Text Color"
              value={state.text_color || '#ffffff'}
              onChange={(v) => onChange('text_color', v)}
            />
            <RangeField
              label="Border Radius"
              min={2}
              max={9999}
              unit="px"
              value={state.border_radius ?? 9999}
              onChange={(v) => onChange('border_radius', v)}
            />
          </div>

          <SectionHeader title="Border & Neon Glow" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Border Width"
              min={0}
              max={4}
              unit="px"
              value={state.border_width ?? 1}
              onChange={(v) => onChange('border_width', v)}
            />
            <ColorField
              label="Border Color"
              value={state.border_color || 'rgba(255, 255, 255, 0.2)'}
              onChange={(v) => onChange('border_color', v)}
            />
          </div>

          <SelectField
            label="Badge Glow Effect"
            value={state.glow || 'none'}
            options={[
              { value: 'none', label: 'No Glow' },
              { value: '0 0 10px rgba(16, 185, 129, 0.5)', label: 'Emerald Glow (0 0 10px)' },
              { value: '0 0 12px rgba(251, 191, 36, 0.6)', label: 'Gold Glow (0 0 12px)' },
              { value: '0 0 12px rgba(56, 189, 248, 0.6)', label: 'Electric Blue Glow' }
            ]}
            onChange={(v) => onChange('glow', v)}
          />

          <SectionHeader title="Badge Typography" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Font Size"
              min={9}
              max={15}
              unit="px"
              value={state.font_size ?? 11}
              onChange={(v) => onChange('font_size', v)}
            />
            <RangeField
              label="Letter Spacing"
              min={-1}
              max={3}
              step={0.5}
              unit="px"
              value={state.letter_spacing ?? 0.5}
              onChange={(v) => onChange('letter_spacing', v)}
            />
          </div>
        </>
      );

    case 'content_panels':
      return (
        <>
          <SectionHeader title="Panel Surface & Material" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <SelectField
              label="Surface Mode"
              value={state.bg_type || 'glass'}
              options={[
                { value: 'glass', label: 'Glassmorphism (Frosted)' },
                { value: 'solid', label: 'Solid Color' },
                { value: 'gradient', label: 'Gradient' }
              ]}
              onChange={(v) => onChange('bg_type', v)}
            />
            {state.bg_type === 'gradient' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={labelStyle}>Gradient</label>
                <button
                  type="button"
                  onClick={() => onOpenGradientModal('bg_gradient', state.bg_gradient)}
                  style={buttonStyle}
                >
                  Edit Panel Gradient
                </button>
              </div>
            ) : (
              <ColorField
                label="Surface Color"
                value={state.bg_color || '#111827'}
                onChange={(v) => onChange('bg_color', v)}
              />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Opacity"
              min={20}
              max={100}
              unit="%"
              value={state.opacity ?? 90}
              onChange={(v) => onChange('opacity', v)}
            />
            <RangeField
              label="Backdrop Blur"
              min={0}
              max={30}
              unit="px"
              value={state.blur ?? 16}
              onChange={(v) => onChange('blur', v)}
            />
          </div>

          <SectionHeader title="Corner Radius & Borders" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <RangeField
              label="Corner Radius"
              min={0}
              max={32}
              unit="px"
              value={state.border_radius ?? 16}
              onChange={(v) => onChange('border_radius', v)}
            />
            <RangeField
              label="Border Width"
              min={0}
              max={6}
              unit="px"
              value={state.border_width ?? 1}
              onChange={(v) => onChange('border_width', v)}
            />
          </div>

          <ColorField
            label="Border Color"
            value={state.border_color || 'rgba(255, 255, 255, 0.08)'}
            onChange={(v) => onChange('border_color', v)}
          />

          <SectionHeader title="Depth & Glow" />
          <SelectField
            label="Box Shadow"
            value={state.shadow || '0 10px 25px rgba(0, 0, 0, 0.5)'}
            options={[
              { value: 'none', label: 'Flat (None)' },
              { value: '0 4px 12px rgba(0, 0, 0, 0.25)', label: 'Subtle Elevation' },
              { value: '0 10px 25px rgba(0, 0, 0, 0.5)', label: 'Medium Float' },
              { value: '0 20px 40px rgba(0, 0, 0, 0.7)', label: 'Deep Floating 3D' }
            ]}
            onChange={(v) => onChange('shadow', v)}
          />
        </>
      );

    case 'typography':
      return (
        <>
          <SectionHeader title="Font Family & Alignments" />
          <SelectField
            label="Font Family"
            value={state.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"}
            options={[
              { value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", label: 'System Default (-apple-system)' },
              { value: "'Inter', sans-serif", label: 'Inter (Modern Sans)' },
              { value: "'Roboto', sans-serif", label: 'Roboto' },
              { value: "'Outfit', sans-serif", label: 'Outfit (Geometric Display)' },
              { value: "Georgia, serif", label: 'Georgia (Classic Serif)' },
              { value: "'JetBrains Mono', monospace", label: 'JetBrains (Monospace Tech)' }
            ]}
            onChange={(v) => onChange('font_family', v)}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <ColorField
              label="Headings Color"
              value={state.heading_color || '#ffffff'}
              onChange={(v) => onChange('heading_color', v)}
            />
            <ColorField
              label="Body Text Color"
              value={state.body_color || '#94a3b8'}
              onChange={(v) => onChange('body_color', v)}
            />
            <ColorField
              label="Accent Color"
              value={state.accent_color || '#fbbf24'}
              onChange={(v) => onChange('accent_color', v)}
            />
          </div>

          <RangeField
            label="Letter Spacing Adjustment"
            min={-2}
            max={4}
            step={0.2}
            unit="px"
            value={state.letter_spacing ?? -0.2}
            onChange={(v) => onChange('letter_spacing', v)}
          />
        </>
      );

    default:
      return null;
  }
}

// ============================================================================
// MOCK PAGE CONTENT RENDERERS FOR ALL MODES AND PAGES
// ============================================================================
function renderMockScreenContent({
  mode,
  page,
  getInteractiveProps,
  panelStyle,
  badgeStyle,
  typoStyle,
  bannerStyle,
  user,
  tenant,
  mobileStyles,
  selectedElement,
  onSelectElement,
  onNavigate,
  cacheBusterKey,
  withCacheBuster
}) {
  // Mobile Login & Sign In Screen Mockup (for all audience modes)
  if (page === 'login') {
    return (
      <div
        data-testid="interactive-mockup-login-page"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          boxSizing: 'border-box',
          padding: '16px 16px 24px 16px'
        }}
      >
        {/* Interactive Banner: Brand Logo & Title */}
        <div
          {...getInteractiveProps('banner')}
          data-testid="interactive-mockup-banner"
          style={{
            ...getInteractiveProps('banner').style,
            width: '100%',
            textAlign: 'center',
            marginBottom: '18px',
            padding: '8px 12px',
            borderRadius: '12px',
            boxSizing: 'border-box'
          }}
        >
          {bannerStyle?.custom_logo_url || tenant?.logo_url ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 16px',
                borderRadius: '14px',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                marginBottom: '10px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                transform: `translate(${bannerStyle?.logo_offset_x || 0}px, ${bannerStyle?.logo_offset_y || 0}px)`
              }}
            >
              <img
                src={withCacheBuster ? withCacheBuster(bannerStyle?.custom_logo_url || tenant?.logo_url) : (bannerStyle?.custom_logo_url || tenant?.logo_url)}
                alt="Brand Logo"
                style={{
                  height: `${bannerStyle?.logo_height || 42}px`,
                  maxHeight: `${bannerStyle?.logo_height || 42}px`,
                  maxWidth: '160px',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: `${bannerStyle?.logo_height || 46}px`,
                height: `${bannerStyle?.logo_height || 46}px`,
                borderRadius: '14px',
                backgroundColor: tenant?.branding_colors?.primary || '#d97706',
                color: '#ffffff',
                marginBottom: '10px',
                fontWeight: '800',
                fontSize: '18px',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
                transform: `translate(${bannerStyle?.logo_offset_x || 0}px, ${bannerStyle?.logo_offset_y || 0}px)`
              }}
            >
              TC
            </div>
          )}

          <h2
            style={{
              fontSize: `${bannerStyle?.title_font_size || 20}px`,
              fontWeight: 800,
              color: bannerStyle?.title_color || typoStyle?.heading_color || '#ffffff',
              margin: '0 0 4px 0',
              letterSpacing: `${bannerStyle?.title_letter_spacing || -0.3}px`
            }}
          >
            {tenant?.brand_name || 'TrophyCoin'}
          </h2>
          <p
            style={{
              fontSize: '12px',
              color: bannerStyle?.subtitle_color || typoStyle?.body_color || '#94a3b8',
              margin: '0 auto',
              lineHeight: 1.4,
              maxWidth: '260px'
            }}
          >
            Access your trophy vault, order custom keepsakes & claim cards
          </p>
        </div>

        {/* Interactive Content Panel: Form Card */}
        <div
          {...getInteractiveProps('content_panels')}
          data-testid="interactive-mockup-panel"
          style={{
            ...getInteractiveProps('content_panels').style,
            ...panelStyle,
            width: '100%',
            boxSizing: 'border-box',
            padding: '18px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}
        >
          {/* Segmented Switcher / Active Toggle with Badges styling */}
          <div
            style={{
              display: 'flex',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '3px'
            }}
          >
            <div
              {...getInteractiveProps('badges')}
              data-testid="interactive-mockup-badge"
              style={{
                ...getInteractiveProps('badges').style,
                ...badgeStyle,
                flex: 1,
                textAlign: 'center',
                padding: '8px 0',
                borderRadius: '9px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Sign In
            </div>
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '8px 0',
                borderRadius: '9px',
                fontSize: '12px',
                fontWeight: 700,
                color: typoStyle?.body_color || '#94a3b8',
                cursor: 'pointer'
              }}
            >
              Create Account
            </div>
          </div>

          {/* Dummy Inputs showcasing typography */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label
              {...getInteractiveProps('typography')}
              style={{
                ...getInteractiveProps('typography').style,
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: typoStyle?.body_color || '#94a3b8'
              }}
            >
              Email Address
            </label>
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '13px',
                color: '#ffffff'
              }}
            >
              collector@trophycoin.io
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label
              {...getInteractiveProps('typography')}
              style={{
                ...getInteractiveProps('typography').style,
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: typoStyle?.body_color || '#94a3b8'
              }}
            >
              Password
            </label>
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '13px',
                color: '#94a3b8',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>••••••••••••</span>
              <span style={{ fontSize: '11px', color: typoStyle?.accent_color || '#38bdf8' }}>Show</span>
            </div>
          </div>

          {/* Primary Action Button */}
          <button
            type="button"
            style={{
              width: '100%',
              marginTop: '4px',
              padding: '12px 16px',
              borderRadius: '10px',
              backgroundColor: typoStyle?.accent_color || tenant?.branding_colors?.primary || '#d97706',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 800,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: `0 4px 16px ${(typoStyle?.accent_color || tenant?.branding_colors?.primary || '#d97706')}40`
            }}
          >
            <span>Sign In to Vault</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          {/* Security Badge */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
            <span
              {...getInteractiveProps('badges')}
              style={{
                ...getInteractiveProps('badges').style,
                ...badgeStyle,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 10px',
                fontSize: '10px',
                letterSpacing: '0.04em'
              }}
            >
              🔒 256-BIT ENCRYPTED VAULT
            </span>
          </div>
        </div>

        {/* Switch to Desktop Site Preview */}
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              fontSize: '11px',
              color: typoStyle?.body_color || '#94a3b8'
            }}
          >
            Switch to Desktop Site
          </span>
        </div>
      </div>
    );
  }

  // 1. END USER PAGES
  if (mode === 'end_user') {
    if (page === 'vault') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '800', color: typoStyle.heading_color || '#fff' }}>
              Your Trophy Collection
            </span>
            <span
              {...getInteractiveProps('badges')}
              style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '2px 8px', fontSize: '10px' }}
            >
              {SAMPLE_OPERATOR_METRICS.top_cards.length} TROPHIES
            </span>
          </div>

          <MobileVaultCarousel
            cards={SAMPLE_OPERATOR_METRICS.top_cards}
            loading={false}
            isOperator={false}
            totalCount={SAMPLE_OPERATOR_METRICS.top_cards.length}
            onSelectCard={() => onNavigate && onNavigate('card')}
            onRefresh={() => {}}
            cacheBusterKey={cacheBusterKey}
          />
        </div>
      );
    }

    if (page === 'card') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center', width: '100%' }}>
          <div
            {...getInteractiveProps('content_panels')}
            style={{
              ...getInteractiveProps('content_panels').style,
              ...panelStyle,
              padding: '16px',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <span
                {...getInteractiveProps('badges')}
                style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '3px 8px', fontSize: '10px' }}
              >
                #1 RANKED
              </span>
              <span style={{ fontSize: '11px', color: typoStyle.body_color || '#94a3b8' }}>
                TC-88219-FT
              </span>
            </div>

            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '350 / 490',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(0,0,0,0.6)'
            }}>
              <img
                src={withCacheBuster ? withCacheBuster('/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg') : '/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg'}
                alt="Trophy Card"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            <div style={{ textAlign: 'center', width: '100%' }}>
              <h3 style={{ margin: '4px 0 2px 0', fontSize: '16px', fontWeight: '800', color: typoStyle.heading_color || '#fff' }}>
                Trophy Largemouth Bass (8.4 lbs)
              </h3>
              <p style={{ margin: 0, fontSize: '11.5px', color: typoStyle.body_color || '#94a3b8' }}>
                Issued Aug 4, 2026 &bull; Fisherman's Tale
              </p>
            </div>

            {/* 3 Clean Action Icon Buttons below loaded card */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '4px' }}>
              <button
                type="button"
                title="Back to Carousel"
                onClick={() => onNavigate && onNavigate('vault')}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
              <button
                type="button"
                title="Share"
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>
              <button
                type="button"
                title="Stats"
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (page === 'shop') {
      return (
        <MobileShopTab
          user={user}
          tenant={tenant}
          cards={SAMPLE_OPERATOR_METRICS.top_cards}
          initialSelectedCard={SAMPLE_OPERATOR_METRICS.top_cards[0]}
        />
      );
    }

    if (page === 'claim') {
      return (
        <MobileClaimTab user={user} onCardClaimed={() => onNavigate && onNavigate('vault')} />
      );
    }

    if (page === 'profile') {
      return (
        <MobileProfileTab
          user={user}
          tenant={tenant}
          onUserUpdated={() => {}}
          onSwitchToDesktop={() => {}}
          onLogout={() => {}}
        />
      );
    }
  }

  // 2. OPERATOR PAGES
  if (mode === 'operator') {
    if (page === 'home') {
      return (
        <MobileOperatorDashboard
          user={user}
          tenant={tenant}
          mobileStyles={mobileStyles}
          onElementClick={onSelectElement}
          selectedElement={selectedElement}
          overrideMetrics={SAMPLE_OPERATOR_METRICS}
          onSelectCard={() => onNavigate && onNavigate('card')}
          onNavigateToVault={() => onNavigate && onNavigate('vault')}
          cacheBusterKey={cacheBusterKey}
        />
      );
    }

    if (page === 'vault') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '800', color: typoStyle.heading_color || '#fff' }}>
              Allocated Vault
            </span>
            <span
              {...getInteractiveProps('badges')}
              style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '2px 8px', fontSize: '10px' }}
            >
              {SAMPLE_OPERATOR_METRICS.top_cards.length} COINS
            </span>
          </div>

          <MobileVaultCarousel
            cards={SAMPLE_OPERATOR_METRICS.top_cards}
            loading={false}
            isOperator={true}
            totalCount={SAMPLE_OPERATOR_METRICS.top_cards.length}
            onSelectCard={() => onNavigate && onNavigate('card')}
            onRefresh={() => {}}
            cacheBusterKey={cacheBusterKey}
          />
        </div>
      );
    }

    if (page === 'card') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center', width: '100%' }}>
          <div
            {...getInteractiveProps('content_panels')}
            style={{
              ...getInteractiveProps('content_panels').style,
              ...panelStyle,
              padding: '16px',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <span
                {...getInteractiveProps('badges')}
                style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '3px 8px', fontSize: '10px' }}
              >
                OPERATOR AUDIT &bull; QA PASSED
              </span>
              <span style={{ fontSize: '11px', color: typoStyle.body_color || '#94a3b8' }}>
                TC-88219-FT
              </span>
            </div>

            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '350 / 490',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(0,0,0,0.6)'
            }}>
              <img
                src={withCacheBuster ? withCacheBuster('/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg') : '/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg'}
                alt="Trophy Card"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            <div style={{ textAlign: 'center', width: '100%' }}>
              <h3 style={{ margin: '4px 0 2px 0', fontSize: '16px', fontWeight: '800', color: typoStyle.heading_color || '#fff' }}>
                Trophy Largemouth Bass (8.4 lbs)
              </h3>
              <p style={{ margin: 0, fontSize: '11.5px', color: typoStyle.body_color || '#94a3b8' }}>
                Assigned to: {user?.first_name || 'Patrick'} &bull; Coin #001
              </p>
            </div>

            {/* 3 Clean Action Icon Buttons below loaded card */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '4px' }}>
              <button
                type="button"
                title="Back to Vault"
                onClick={() => onNavigate && onNavigate('vault')}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
              <button
                type="button"
                title="Share Card"
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>
              <button
                type="button"
                title="View Stats"
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (page === 'profile') {
      return (
        <MobileProfileTab
          user={user}
          tenant={tenant}
          onUserUpdated={() => {}}
          onSwitchToDesktop={() => {}}
          onLogout={() => {}}
        />
      );
    }
  }

  // 3. AGENT PAGES
  if (mode === 'agent') {
    if (page === 'verify') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div
            {...getInteractiveProps('content_panels')}
            style={{
              ...getInteractiveProps('content_panels').style,
              ...panelStyle,
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                border: '2px dashed #38bdf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '34px',
                animation: 'pulse 2s infinite'
              }}
            >
              📡
            </div>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff' }}>
              NFC Field Verification
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              Hold terminal near physical Trophy Coin transponder.
            </div>
            <span
              {...getInteractiveProps('badges')}
              style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '4px 12px', fontSize: '11px', marginTop: '6px' }}
            >
              READY TO SCAN
            </span>
          </div>
        </div>
      );
    }

    if (page === 'orders') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: '800', color: '#fff' }}>Assigned Handover Orders</div>
          {[
            { id: '#9021', name: '4x Trophy Plaques for Marina Cup', status: 'READY FOR HANDOVER' },
            { id: '#9024', name: '1x Royal Stag Gold Medallion', status: 'IN TRANSIT' }
          ].map((ord, idx) => (
            <div
              key={idx}
              {...getInteractiveProps('content_panels')}
              style={{ ...getInteractiveProps('content_panels').style, ...panelStyle, padding: '12px 14px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#fff' }}>Order {ord.id}</span>
                <span
                  {...getInteractiveProps('badges')}
                  style={{ ...getInteractiveProps('badges').style, ...badgeStyle, padding: '2px 6px', fontSize: '9px' }}
                >
                  {ord.status}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>{ord.name}</div>
            </div>
          ))}
        </div>
      );
    }

    if (page === 'profile') {
      return (
        <div
          {...getInteractiveProps('content_panels')}
          style={{ ...getInteractiveProps('content_panels').style, ...panelStyle, padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff' }}>Agent Terminal Status</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Terminal ID: AGENT-FIELD-09 &bull; Mesh Status: Active</div>
        </div>
      );
    }
  }

  return null;
}

// ============================================================================
// REUSABLE FORM CONTROLS & STYLES
// ============================================================================
const labelStyle = {
  fontSize: '12px',
  fontWeight: '600',
  color: 'var(--text-main, #f0f6fc)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const inputStyle = {
  height: '36px',
  padding: '0 10px',
  borderRadius: '6px',
  border: '1px solid var(--border-color, rgba(255,255,255,0.12))',
  background: 'var(--input-bg, #0d1117)',
  color: 'var(--input-text, #f0f6fc)',
  fontSize: '12.5px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box'
};

const buttonStyle = {
  height: '36px',
  padding: '0 12px',
  borderRadius: '6px',
  border: '1px solid var(--border-color, rgba(255,255,255,0.15))',
  background: 'rgba(255,255,255,0.06)',
  color: 'var(--text-main, #f0f6fc)',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer'
};

function SectionHeader({ title }) {
  return (
    <div
      style={{
        fontSize: '11px',
        fontWeight: '800',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#38bdf8',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        paddingBottom: '4px',
        marginTop: '6px'
      }}
    >
      {title}
    </div>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={labelStyle}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, cursor: 'pointer' }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="color"
          value={value?.startsWith('#') && value.length === 7 ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '36px',
            height: '36px',
            padding: '0',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
            background: 'none',
            cursor: 'pointer'
          }}
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
    </div>
  );
}

function RangeField({ label, min, max, step = 1, unit = '', value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={labelStyle}>
        <span>{label}</span>
        <span style={{ color: '#38bdf8', fontWeight: '700' }}>
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ height: '5px', accentColor: '#38bdf8', cursor: 'pointer', marginTop: '6px' }}
      />
    </div>
  );
}
